// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Who may open the monitoring module and the RPM Command Center.
 *
 * READ THIS BEFORE RELYING ON IT. This is navigation scoping, not access
 * control. It decides what appears in a nav and which routes render; it does
 * nothing to stop a determined user querying the API directly, because an SPA
 * cannot. Real enforcement is an AccessPolicy on the server, and until that
 * exists these gates reduce clutter and accidental access — which is worth
 * having — but must not be described to a customer as a permission boundary.
 *
 * Three layers, and they answer different questions:
 *
 * 1. Entitlement — did the practice buy this module? A commercial fact, held
 *    on the organization. Not wired: the chain that would write it is broken
 *    in the admin app, so this layer currently answers "unknown" and is
 *    treated as permissive. That is deliberate and recorded rather than
 *    silently defaulted.
 * 2. Grant — has this specific person been given the module? An explicit
 *    extension on their PractitionerRole, which is what makes access
 *    per-user rather than per-role.
 * 3. Role default — if nobody has decided, does this kind of job plausibly
 *    involve remote monitoring? A fallback so a new tenant is usable before
 *    anyone has granted anything, never an override of an explicit grant.
 */

import type { Organization, PractitionerRole } from '@medplum/fhirtypes';
import type { StaffRoleCode } from '../types/staff';

/** The modules access can be granted to. */
export type MonitoringModuleKey = 'rpm' | 'command-center';

/** Explicit per-user grants live here. */
export const MODULE_GRANT_EXT =
  'http://nuloucarepoint.com/fhir/StructureDefinition/granted-modules';

/** Tenant entitlement lives here — written by provisioning, when it works. */
export const ENTITLEMENT_EXT =
  'http://nuloucarepoint.com/fhir/StructureDefinition/enabled-modules';

/**
 * Roles that get monitoring by default.
 *
 * Chosen by whether the job plausibly involves reading a patient's readings or
 * logging care-management time. Front desk, schedulers, coders and collections
 * are absent — not as a judgement about them, but because a monitoring
 * worklist is a list of patients with clinical detail attached, and the default
 * for a role that has no reason to read it should be no.
 *
 * A practice that disagrees grants the module explicitly, which is the point of
 * layer 2 — this list is a starting position, not a policy.
 */
const DEFAULT_MONITORING_ROLES: ReadonlySet<StaffRoleCode> = new Set<StaffRoleCode>([
  'physician',
  'nurse-practitioner',
  'physician-assistant',
  'psychiatrist',
  'registered-nurse',
  'licensed-practical-nurse',
  'medical-assistant',
  'care-coordinator',
  'social-worker',
  'respiratory-therapist',
  'physical-therapist',
  'occupational-therapist',
  'dietitian',
  'practice-administrator',
  'practice-owner',
  'office-manager',
]);

/**
 * The Command Center is narrower than the module by default.
 *
 * A wall shows every alerting patient in the tenant at once, which is a
 * different exposure from a worklist someone works through — so the default is
 * the people who run the operation rather than everyone who can open a chart.
 */
const DEFAULT_COMMAND_CENTER_ROLES: ReadonlySet<StaffRoleCode> = new Set<StaffRoleCode>([
  'physician',
  'nurse-practitioner',
  'registered-nurse',
  'care-coordinator',
  'practice-administrator',
  'practice-owner',
  'office-manager',
]);

export interface MonitoringAccess {
  readonly rpm: boolean;
  readonly commandCenter: boolean;
  /** Why, in words, for a settings screen and for support conversations. */
  readonly reason: string;
  /** True when the decision came from a role default rather than a decision. */
  readonly fromDefault: boolean;
}

/** Module keys explicitly granted to this role assignment. */
export function grantedModules(role: PractitionerRole | undefined): MonitoringModuleKey[] {
  const extension = role?.extension?.find((entry) => entry.url === MODULE_GRANT_EXT);
  return (extension?.extension ?? [])
    .map((entry) => entry.valueCode)
    .filter((code): code is MonitoringModuleKey => code === 'rpm' || code === 'command-center');
}

/** Modules the organization is entitled to, or undefined when unrecorded. */
export function entitledModules(
  organization: Organization | undefined
): string[] | undefined {
  const extension = organization?.extension?.find((entry) => entry.url === ENTITLEMENT_EXT);
  if (!extension) {
    return undefined;
  }
  return (extension.extension ?? [])
    .map((entry) => entry.valueCode)
    .filter((code): code is string => Boolean(code));
}

/** The staff role codes on a PractitionerRole. */
export function roleCodes(role: PractitionerRole | undefined): StaffRoleCode[] {
  return (role?.code ?? [])
    .flatMap((concept) => concept.coding ?? [])
    .map((coding) => coding.code)
    .filter((code): code is StaffRoleCode => Boolean(code));
}

export function resolveMonitoringAccess(options: {
  readonly role?: PractitionerRole;
  readonly organization?: Organization;
  /** Project admins are not gated: they administer the tenant. */
  readonly isProjectAdmin?: boolean;
}): MonitoringAccess {
  const { role, organization, isProjectAdmin } = options;

  if (isProjectAdmin) {
    return {
      rpm: true,
      commandCenter: true,
      reason: 'Project administrator — not gated.',
      fromDefault: false,
    };
  }

  const entitled = entitledModules(organization);
  if (entitled !== undefined && !entitled.includes('rpm')) {
    /*
     * The only case where entitlement actually decides anything today: an
     * organization that has recorded a module list and left RPM out of it.
     * An absent list means unknown, not denied — see the header.
     */
    return {
      rpm: false,
      commandCenter: false,
      reason: 'This practice is not entitled to the monitoring module.',
      fromDefault: false,
    };
  }

  const granted = grantedModules(role);
  if (granted.length > 0) {
    return {
      rpm: granted.includes('rpm'),
      commandCenter: granted.includes('command-center'),
      reason: 'Granted explicitly on your role assignment.',
      fromDefault: false,
    };
  }

  const codes = roleCodes(role);
  const rpm = codes.some((code) => DEFAULT_MONITORING_ROLES.has(code));
  const commandCenter = codes.some((code) => DEFAULT_COMMAND_CENTER_ROLES.has(code));

  return {
    rpm,
    commandCenter,
    reason:
      codes.length === 0
        ? 'No role recorded for you at this practice, so the default applies.'
        : `Default for ${codes.join(', ')}. A practice can grant or remove this per person.`,
    fromDefault: true,
  };
}
